import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OrderTypeEnums } from 'src/app/enum/order-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ConsolidatedEquipmentAvailabilityTableModel } from 'src/app/models/tables/preliminaries/consolidated-equipment-availability-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ConsolidatedEquipmentAvailabilityService } from 'src/app/services/preliminaries/consolidated-equipment-availability.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ConsolidatedAvailabilityReportComponent } from '../consolidated-availability-report/consolidated-availability-report.component';


@Component({
  selector: 'app-consolidated-equipment-availability',
  templateUrl: './consolidated-equipment-availability.component.html',
  styleUrls: ['./consolidated-equipment-availability.component.scss'],
  standalone: false
})
export class ConsolidatedEquipmentAvailabilityComponent implements OnInit {

  private readonly headerService = inject(HeaderService);
  private readonly consolidatedEquipmentAvailabilityService = inject(ConsolidatedEquipmentAvailabilityService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  @ViewChild('consolidatedAvailabilityReport') consolidatedAvailabilityReportModalComponent?: CustomModalComponent | null = null;
  ascendant: boolean = true;
  columnSort: string | null = null;
  componentTableReport = ConsolidatedAvailabilityReportComponent;
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<ConsolidatedEquipmentAvailabilityTableModel> =
    new PagedResponseModel<ConsolidatedEquipmentAvailabilityTableModel>();
  generalHeader: FilterGlobalResponseModel = new FilterGlobalResponseModel();
  headerTitleModal: string = 'Equipo Disponible - Consolidado Detallado';
  inputsForReportModals$ = new BehaviorSubject<any>({
    articleId: 0,
  });
  isOpened: string | undefined = undefined;
  limit: number = 10;
  listOrderType: ColumnResponseModel = new ColumnResponseModel();
  nameWindow: string = 'Disponible Consolidado';
  orderTypeId: number = OrderTypeEnums.rental;
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Ver detalle',
      icon: 'pi pi-eye',
      action: 'view'
    }
  ];
  url: string = 'consolidated-equipment-availability';

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getPermission(),
      this.getColumnsForGeneralConsultation(),
      this.getConsolidatedEquipmentAvailability(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.consolidatedEquipmentAvailabilityService.exportGeneralConsultation(this.filters,
        this.columnSort, this.ascendant, this.orderTypeId
      ));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Disponible Consolidado.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar disponible consolidado.', error);
    }
  }

  async getConsolidatedEquipmentAvailability() {
    try {
      const response = await firstValueFrom(this.consolidatedEquipmentAvailabilityService.getConsolidatedEquipmentAvailability(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters, this.orderTypeId
      ));
      this.generalData = response;
    } catch (error) {
      console.error('Error al buscar los equipos disponibles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.columns.length === 0) {
        const response = await firstValueFrom(this.consolidatedEquipmentAvailabilityService.getColumnsForGeneralConsultation());
        this.listOrderType = response.filtersGlobal[0];
        this.generalHeader = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async onAction(event: { action: string, item?: ConsolidatedEquipmentAvailabilityTableModel, selectedItems?: number[] }) {
    switch (event.action) {
      case 'view':
        this.inputsForReportModals$.next({
          articleId: event.item?.articleId,
        });
        this.consolidatedAvailabilityReportModalComponent?.show();
        break;
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getConsolidatedEquipmentAvailability();
  }

  onOrderTypeChange(value: number) {
    this.orderTypeId = value;
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
